import { ReactElement, useState, ChangeEvent, useEffect } from "react";
import {
  RiverTextInput,
  RiverDialog,
  IValidationErrors,
  useValidation,
  useNotification,
} from "@river/common-ui";
import AddIcon from "@mui/icons-material/Add";
import { ICustomer, ISite, SiteDto } from "@river/interfaces";
import EditIcon from "@mui/icons-material/Edit";
import { customerService } from "../../services";
import styles from "./add-site-dialog.module.scss";

interface IAddSiteDialogProps {
  open: boolean;
  customer: ICustomer;
  onClose: (success: boolean) => void;
  siteToEdit?: ISite;
}

export const AddSiteDialog = ({
  open,
  customer,
  onClose,
  siteToEdit,
}: IAddSiteDialogProps): ReactElement => {
  const [site, setSite] = useState<SiteDto>(siteToEdit ?? new SiteDto());
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({
    fields: {},
    list: [],
  });
  const notify = useNotification();
  const { validateFields, validateProperty } = useValidation();

  const resetValidationErrors = (): void =>
    setValidationErrors({ fields: {}, list: [] });

  const resetDialogState = (): void => {
    resetValidationErrors();
    setSite(new SiteDto());
  };

  const closeDialog = (success: boolean): void => {
    resetDialogState();
    onClose(success);
  };

  const onPropertyChange = async (
    event: ChangeEvent<{ name?: string; value: unknown }>
  ): Promise<void> => {
    let newState: SiteDto = {
      ...site,
      [event.target.name!]: event.target.value,
    };

    Object.setPrototypeOf(newState, Object.getPrototypeOf(site));
    setSite(newState);
    const newErrors: IValidationErrors = await validateProperty(
      newState,
      event.target.name!,
      validationErrors
    );
    setValidationErrors(newErrors);
  };

  const validateSite = async (Site: SiteDto): Promise<IValidationErrors> => {
    const errors = await validateFields(Site);
    setValidationErrors(errors);
    return errors;
  };

  const addSite = async (): Promise<void> => {
    const errors: IValidationErrors = await validateSite(site);
    if (errors.list.length > 0) {
      return;
    }

    try {
      if (site?._id) {
        await customerService.updateCustomerSite(
          customer._id!,
          site?._id!,
          site
        );
      } else {
        await customerService.createCustomerSite(customer._id!, site);
      }
      const success = true;
      closeDialog(success);
    } catch (message) {
      notify.error({ message });
    }
  };

  useEffect(() => {
    if (siteToEdit) {
      const edit = Object.setPrototypeOf(
        siteToEdit,
        Object.getPrototypeOf(site)
      );

      setSite(edit);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteToEdit]);

  const renderSiteField = (): ReactElement => (
    <div className={styles.fieldContainer}>
      <RiverTextInput
        id="site"
        label="Site"
        value={site.site || ""}
        onChangeEvent={(event) => onPropertyChange(event)}
        validationErrors={validationErrors?.fields["site"]}
        fullWidth
      />
      {validationErrors?.fields["site"]?.errors[0].message && (
        <span className={styles.errorMessage}>
          {validationErrors?.fields["site"]?.errors[0].message}
        </span>
      )}
    </div>
  );

  return (
    <RiverDialog
      titleProps={{
        title: `${siteToEdit ? "Edit Site" : "Add a New Site"}`,
        icon: siteToEdit ? EditIcon : AddIcon,
      }}
      open={open}
      onClose={() => {
        const success = false;
        closeDialog(success);
      }}
      closeButtonText="Close"
      actionButtonText={siteToEdit ? "Edit" : "Add"}
      showActionsDivider={false}
      onSubmit={addSite}
    >
      {renderSiteField()}
    </RiverDialog>
  );
};
